import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { ArrowLeft, User, BookOpen, CreditCard, BarChart3, Heart, FileText, Download, ArrowRightLeft, ClipboardList, Camera, Pencil, PowerOff, Eye, X, Settings } from 'lucide-react';
import { toast } from 'sonner';
import type { Aluno, TagRfid } from '../../types/models';
import { api } from '../../api/client';
import { formatCPF, formatPhone } from '../../utils/cpfUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { hasPermission } from '../../auth/permissions';
import {
  formatPresencaDate,
  presencaDayNumber,
  useFrequenciaAluno,
} from '../../api/hooks/usePresencasDiarias';
import { useCrud } from '../../api/hooks/useCrud';
import '../../components/ui/datatable.css';
import './responsavel-ficha-desktop.css';
import { ROUTES } from '../../api/routes';
import { JustificativasAlunoPanel } from './JustificativasAlunoPanel';
import { Modal } from '../../components/ui/Modal';
import {
  ResponsavelFormModal,
  type ResponsavelFormModel,
} from './ResponsavelFormModal';
import { ResponsavelFichaDesktop } from './ResponsavelFichaDesktop';
import {
  AppStatusBadge,
  getResponsavelAppStatus,
  ResponsavelAcessoAppPanel,
} from './ResponsavelAcessoApp';

const TABS = [
  { key: 'dados', label: 'Dados pessoais', icon: User },
  { key: 'responsavel', label: 'Responsável', icon: Heart },
  { key: 'academico', label: 'Acadêmico', icon: BookOpen },
  { key: 'boletim', label: 'Boletim', icon: ClipboardList },
  { key: 'rfid', label: 'RFID / Acesso', icon: CreditCard },
  { key: 'frequencia', label: 'Frequência', icon: BarChart3 },
  { key: 'acoes', label: 'Ações', icon: Settings },
] as const;

const SITUACAO_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  ATIVO: { label: 'Ativo', color: '#16a34a', bg: '#dcfce7' },
  TRANSFERIDO: { label: 'Transferido', color: '#d97706', bg: '#fef3c7' },
  EVADIDO: { label: 'Evadido', color: '#dc2626', bg: '#fee2e2' },
  FORMADO: { label: 'Formado', color: '#2563eb', bg: '#dbeafe' },
  DESISTENTE: { label: 'Desistente', color: '#9333ea', bg: '#f3e8ff' },
  FALECIDO: { label: 'Falecido', color: '#6b7280', bg: '#f3f4f6' },
};


export function AlunoFichaPage({
  overrideId,
  onClose,
  onEdit,
  onDeactivate,
  readOnly,
  mobileLayout,
}: {
  overrideId?: string;
  onClose?: () => void;
  onEdit?: () => void;
  onDeactivate?: () => void;
  /** Professor: somente consulta, sem ações cadastrais */
  readOnly?: boolean;
  /** Dentro de MobilePageWrapper — layout 1 coluna e sem botão voltar duplicado */
  mobileLayout?: boolean;
} = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = overrideId || routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = '/' + (location.pathname.split('/')[1] || 'admin');
  const user = useAuthStore(s => s.user);
  const isReadOnly = readOnly ?? basePath === '/professor';
  const canManageAlunos = !isReadOnly && hasPermission(user, 'alunos.manage');
  const canManageResponsaveis = !isReadOnly && hasPermission(user, 'responsaveis.manage');
  const visibleTabs = isReadOnly
    ? TABS.filter((t) => !['rfid', 'boletim', 'acoes'].includes(t.key))
    : [...TABS];
  const [tab, setTab] = useState<string>('dados');
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('tab');
    if (q && ['dados', 'rfid', 'frequencia', 'academico', 'boletim', 'responsavel', 'acoes'].includes(q)) {
      setTab(q);
    }
  }, [location.search]);
  const escolaId = useAuthStore((s) => s.escolaId);
  const queryClient = useQueryClient();
  const { data: tagsAll } = useCrud<TagRfid>('tags-rfid', undefined, {
    enabled: !isReadOnly,
  });
  const [novaTag, setNovaTag] = useState('');
  const fotoInputRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferMotivo, setTransferMotivo] = useState('');
  const [transferEscola, setTransferEscola] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  // ===== CROP MODAL VIA DOM DIRETO (fora do React tree) =====
  useEffect(() => {
    if (!cropFile) return;
    const imgUrl = URL.createObjectURL(cropFile);
    const VIEWPORT = 240; // px do viewport circular

    const el = document.createElement('div');
    el.id = 'photo-crop-modal';
    el.innerHTML = `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:99999;display:flex;align-items:center;justify-content:center;font-family:Inter,system-ui,sans-serif" id="crop-overlay">
        <div style="background:white;border-radius:14px;padding:24px 28px;max-width:480px;width:90vw;box-shadow:0 12px 40px rgba(0,0,0,.3);text-align:center" onclick="event.stopPropagation()">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
            <h3 style="margin:0;font-size:1rem;font-weight:700;color:#111827">Recortar Foto</h3>
            <button id="crop-close-btn" style="background:none;border:none;cursor:pointer;color:#6b7280;padding:4px;font-size:1.2rem">&#10005;</button>
          </div>
          <div id="crop-viewport" style="position:relative;width:${VIEWPORT}px;height:${VIEWPORT}px;margin:0 auto 12px;border-radius:50%;overflow:hidden;cursor:grab;border:3px solid #1B365D;background:#e5e7eb;touch-action:none">
            <img id="crop-img" src="${imgUrl}" alt="Crop" style="position:absolute;user-select:none;pointer-events:none" draggable="false" />
          </div>
          <div style="display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:14px">
            <span style="font-size:0.7rem;color:#6b7280">-</span>
            <input id="crop-zoom" type="range" min="100" max="300" value="100" style="width:180px;accent-color:#1B365D" />
            <span style="font-size:0.7rem;color:#6b7280">+</span>
          </div>
          <p style="font-size:0.72rem;color:#9ca3af;margin-bottom:14px">Arraste para posicionar. Use o zoom para ajustar.</p>
          <div style="display:flex;gap:8px;justify-content:center">
            <button id="crop-cancel-btn" style="padding:8px 16px;background:#f3f4f6;color:#374151;border:none;border-radius:8px;font-size:.82rem;font-weight:600;cursor:pointer">Cancelar</button>
            <button id="crop-confirm-btn" style="padding:8px 20px;background:#1B365D;color:white;border:none;border-radius:8px;font-size:.82rem;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:4px">&#10003; Confirmar</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(el);

    const img = el.querySelector('#crop-img') as HTMLImageElement;
    const viewport = el.querySelector('#crop-viewport') as HTMLDivElement;
    const zoomSlider = el.querySelector('#crop-zoom') as HTMLInputElement;
    let zoom = 1, offX = 0, offY = 0, dragging = false, startX = 0, startY = 0, startOffX = 0, startOffY = 0;

    const reposition = () => {
      const w = img.naturalWidth * zoom;
      const h = img.naturalHeight * zoom;
      // Clamp para nao sair do viewport
      const maxX = 0, minX = VIEWPORT - w;
      const maxY = 0, minY = VIEWPORT - h;
      offX = Math.min(maxX, Math.max(minX, offX));
      offY = Math.min(maxY, Math.max(minY, offY));
      img.style.width = w + 'px';
      img.style.height = h + 'px';
      img.style.left = offX + 'px';
      img.style.top = offY + 'px';
    };

    img.onload = () => {
      // Fit: escala para cobrir o viewport
      const scale = Math.max(VIEWPORT / img.naturalWidth, VIEWPORT / img.naturalHeight);
      zoom = scale;
      zoomSlider.min = String(Math.round(scale * 100));
      zoomSlider.value = String(Math.round(scale * 100));
      zoomSlider.max = String(Math.round(scale * 300));
      // Centralizar
      offX = (VIEWPORT - img.naturalWidth * zoom) / 2;
      offY = (VIEWPORT - img.naturalHeight * zoom) / 2;
      reposition();
    };

    zoomSlider.addEventListener('input', () => {
      const oldZoom = zoom;
      zoom = parseInt(zoomSlider.value) / 100;
      // Manter centro ao dar zoom
      const cx = VIEWPORT / 2;
      const cy = VIEWPORT / 2;
      offX = cx - (cx - offX) * (zoom / oldZoom);
      offY = cy - (cy - offY) * (zoom / oldZoom);
      reposition();
    });

    // Drag
    const onDown = (e: MouseEvent | TouchEvent) => {
      dragging = true;
      viewport.style.cursor = 'grabbing';
      const pt = 'touches' in e ? e.touches[0] : e;
      startX = pt.clientX; startY = pt.clientY;
      startOffX = offX; startOffY = offY;
      e.preventDefault();
    };
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!dragging) return;
      const pt = 'touches' in e ? e.touches[0] : e;
      offX = startOffX + (pt.clientX - startX);
      offY = startOffY + (pt.clientY - startY);
      reposition();
    };
    const onUp = () => { dragging = false; viewport.style.cursor = 'grab'; };

    viewport.addEventListener('mousedown', onDown);
    viewport.addEventListener('touchstart', onDown, { passive: false });
    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);

    // Confirm: extract crop from viewport
    const confirmCrop = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = 200; canvas.height = 200;
      const ctx = canvas.getContext('2d');
      if (!ctx || !img.naturalWidth) return;
      // Coordenadas no espaco original da imagem
      const sx = -offX / zoom;
      const sy = -offY / zoom;
      const sSize = VIEWPORT / zoom;
      ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, 200, 200);
      const base64 = canvas.toDataURL('image/jpeg', 0.8);
      setCropFile(null);
      try {
        await api.post(ROUTES.upload.fotoAluno(id!), { fotoBase64: base64 });
        queryClient.invalidateQueries({ queryKey: ['aluno'] });
        toast.success('Foto atualizada!');
      } catch { toast.error('Erro ao enviar foto'); }
    };

    // Event listeners
    const close = () => setCropFile(null);
    el.querySelector('#crop-overlay')?.addEventListener('click', close);
    el.querySelector('#crop-close-btn')?.addEventListener('click', close);
    el.querySelector('#crop-cancel-btn')?.addEventListener('click', close);
    el.querySelector('#crop-confirm-btn')?.addEventListener('click', () => confirmCrop());

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      URL.revokeObjectURL(imgUrl);
      el.remove();
    };
  }, [cropFile, id, queryClient]);

  const { data: alunoData } = useQuery({
    queryKey: ['aluno', escolaId, id],
    queryFn: async () => { const { data } = await api.get(ROUTES.alunos.findOne(escolaId!, id!)); return data as Aluno; },
    enabled: !!id && !!escolaId,
  });

  const { frequencia } = useFrequenciaAluno(id);
  const aluno = alunoData;
  const tagsAluno = (tagsAll ?? []).filter((t) => t.alunoId === id);

  if (!aluno) {
    return (
      <div className="admin-page">
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Carregando ficha do aluno...</div>
      </div>
    );
  }

  const formatDate = (d?: string) => formatPresencaDate(d);

  const vincularTag = async () => {
    if (!novaTag.trim()) return;
    try {
      await api.post(ROUTES.alunos.findOne(escolaId!, id!) + '/tags', { codigo: novaTag.trim() });
      toast.success('Tag RFID vinculada!');
      setNovaTag('');
    } catch {
      toast.error('Erro ao vincular tag');
    }
  };

  /** Download PDF autenticado (via blob) */
  const downloadPdf = async (path: string, filename: string) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const pct = frequencia.percentual;
  const temFreq = frequencia.totalDias > 0;
  const semaforoColor = !temFreq
    ? '#94a3b8'
    : pct >= 85
      ? '#16a34a'
      : pct >= 75
        ? '#d97706'
        : '#dc2626';
  const semaforoLabel = !temFreq
    ? 'Sem registros de presença'
    : pct >= 85
      ? 'Frequência regular'
      : pct >= 75
        ? 'Atenção — frequência abaixo da média'
        : 'ALERTA LDB — Frequência abaixo de 75%';
  const situacaoInfo = SITUACAO_LABELS[aluno.situacao || 'ATIVO'] || SITUACAO_LABELS.ATIVO;
  const turmaLabel = aluno.turma?.nome || 'Sem turma';

  const isModal = !!onClose;
  const pageClass = [
    'aluno-ficha-page',
    'resp-ficha',
    isReadOnly ? 'aluno-ficha-page--readonly' : '',
    mobileLayout ? 'aluno-ficha-page--mobile' : '',
    isModal ? 'aluno-ficha-page--modal' : 'admin-page',
  ]
    .filter(Boolean)
    .join(' ');

  const hasFotoReal =
    !!aluno.fotoUrl &&
    !aluno.fotoUrl.toLowerCase().includes('i.pravatar.cc') &&
    !aluno.fotoUrl.toLowerCase().includes('ui-avatars.com');

  return (
    <div
      className={pageClass}
      style={{
        marginTop: isModal || mobileLayout ? 0 : typeof window !== 'undefined' && window.innerWidth < 768 ? 0 : -24,
      }}
    >
      <header className="resp-ficha-head">
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {hasFotoReal ? (
            <img src={aluno.fotoUrl!} alt="" className="resp-ficha-avatar" />
          ) : (
            <span className="resp-ficha-avatar-ph">
              <User size={32} />
            </span>
          )}
          {canManageAlunos && (
            <button
              type="button"
              className="aluno-ficha-cam-btn"
              onClick={() => fotoInputRef.current?.click()}
              title="Alterar foto"
            >
              <Camera size={11} />
            </button>
          )}
          <input
            ref={fotoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) {
                toast.error('Imagem deve ter no maximo 5MB');
                return;
              }
              setCropFile(file);
              e.target.value = '';
            }}
          />
        </div>
        <div className="resp-ficha-head-main">
          <p className="resp-ficha-eyebrow">Ficha do Aluno</p>
          <h2 className="resp-ficha-name">{aluno.nome}</h2>
          <div className="resp-ficha-meta-line">
            <span className="resp-ficha-sub">
              {aluno.matricula || 'Sem matrícula'}
              {turmaLabel ? ` · ${turmaLabel}` : ''}
              {aluno.turma?.turno ? ` · ${aluno.turma.turno}` : ''}
            </span>
            <span className={`resp-ficha-badge ${aluno.ativo !== false ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}>
              {aluno.ativo !== false ? 'Cadastro ativo' : 'Inativo'}
            </span>
            <span
              className="resp-ficha-badge"
              style={{
                color: situacaoInfo.color,
                borderColor: `${situacaoInfo.color}55`,
                background: situacaoInfo.bg,
              }}
            >
              {situacaoInfo.label}
            </span>
          </div>
        </div>
        {!isModal && !mobileLayout ? (
          <button
            type="button"
            className="resp-ficha-close"
            onClick={() => navigate(`${basePath}/alunos`)}
            aria-label="Voltar"
            title="Voltar"
          >
            <ArrowLeft size={16} />
          </button>
        ) : isModal ? (
          <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </button>
        ) : null}
      </header>

      <div className="resp-ficha-body aluno-ficha-body">
        <div className="resp-ficha-stack">
          <nav className="resp-ficha-tabs resp-ficha-tabs--scroll" aria-label="Secções da ficha">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`resp-ficha-tab${tab === t.key ? ' resp-ficha-tab--active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <Icon size={12} />
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div className="resp-ficha-panel">
      {/* === TAB: Dados Pessoais === */}
      {tab === 'dados' && (
        <div className="aluno-ficha-dados-grid">
          <div className="resp-ficha-card resp-ficha-card--contato">
            <h3 className="resp-ficha-card-title">
              <User size={14} /> Informações pessoais
            </h3>
            <dl className="resp-ficha-dl resp-ficha-dl--contato">
              {[
                { label: 'Nome', value: aluno.nome },
                { label: 'Nascimento', value: formatDate(aluno.dataNascimento) },
                { label: 'Gênero', value: aluno.genero || '—' },
                { label: 'CPF', value: isReadOnly ? '—' : (aluno.cpf || '—') },
                { label: 'Naturalidade', value: aluno.naturalidade || '—' },
                { label: 'Nacionalidade', value: aluno.nacionalidade?.replace(/_/g, ' ') || '—' },
                { label: 'País de origem', value: aluno.paisOrigem || '—' },
                { label: 'Cor/Raça', value: aluno.corRaca?.replace(/_/g, ' ') || '—' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="resp-ficha-card resp-ficha-card--contato">
            <h3 className="resp-ficha-card-title">
              <Heart size={14} /> Saúde e NEE
            </h3>
            <dl className="resp-ficha-dl resp-ficha-dl--contato">
              {[
                { label: 'Tipo sanguíneo', value: aluno.tipoSanguineo || '—' },
                { label: 'Alergias', value: aluno.alergias || 'Nenhuma' },
                { label: 'Restrição alimentar', value: aluno.restricaoAlimentar || 'Nenhuma' },
                { label: 'Plano de saúde', value: aluno.planoSaude || '—' },
                { label: 'Deficiência', value: aluno.tipoDeficiencia?.replace(/_/g, ' ') || 'Nenhuma' },
                {
                  label: 'Detalhe NEE',
                  value: aluno.deficienciaDetalhe || '—',
                  span: true,
                },
              ].map(({ label, value, span }) => (
                <div key={label} className={span ? 'resp-ficha-dl-span' : undefined}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="resp-ficha-card resp-ficha-card--contato aluno-ficha-card--wide">
            <h3 className="resp-ficha-card-title">
              <FileText size={14} /> Endereço
            </h3>
            <dl className="resp-ficha-dl resp-ficha-dl--contato aluno-ficha-dl--endereco">
              {[
                { label: 'CEP', value: isReadOnly ? '—' : (aluno.cep || '—') },
                { label: 'Cidade/UF', value: aluno.cidade ? `${aluno.cidade}/${aluno.uf || ''}` : '—' },
                { label: 'Bairro', value: aluno.bairro || '—' },
                { label: 'Número', value: isReadOnly ? '—' : (aluno.numero || '—') },
                { label: 'Complemento', value: isReadOnly ? '—' : (aluno.complemento || '—') },
                { label: 'Endereço', value: isReadOnly ? '—' : (aluno.endereco || '—'), span: true },
                {
                  label: 'Geolocalização',
                  value:
                    aluno.latitude != null && aluno.longitude != null
                      ? `${Number(aluno.latitude).toFixed(5)}, ${Number(aluno.longitude).toFixed(5)}`
                      : '—',
                  span: true,
                },
              ].map(({ label, value, span }) => (
                <div key={label} className={span ? 'resp-ficha-dl-span' : undefined}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* === TAB: Responsável === */}
      {tab === 'responsavel' && (
        <div className="aluno-ficha-tab-pad">
          <ResponsavelTab aluno={aluno} escolaId={escolaId!} queryClient={queryClient} canManage={canManageResponsaveis} />
        </div>
      )}

      {/* === TAB: Acadêmico (Histórico de Turmas + Mudar Turma) === */}
      {tab === 'academico' && (
        <div className="aluno-ficha-tab-pad">
          <AcademicoTab
            aluno={aluno}
            escolaId={escolaId!}
            queryClient={queryClient}
            canManage={canManageAlunos}
            readOnly={isReadOnly}
          />
        </div>
      )}

      {/* === TAB: Boletim === */}
      {tab === 'boletim' && (
        <div className="aluno-ficha-tab-pad">
          <BoletimTab escolaId={escolaId!} alunoId={id!} />
        </div>
      )}

      {tab === 'rfid' && (
        <div className="aluno-ficha-tab-pad">
          <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
            {tagsAluno.length > 0 ? (
              <ul className="aluno-ficha-list">
                {tagsAluno.map((tag) => (
                  <li key={tag.id} className="aluno-ficha-list-item">
                    <CreditCard size={16} className="aluno-ficha-list-icon" />
                    <div className="aluno-ficha-list-main">
                      <span className="aluno-ficha-list-title aluno-ficha-mono">{tag.codigo}</span>
                      <span className="aluno-ficha-list-meta">{tag.descricao || 'Cartão de acesso'}</span>
                    </div>
                    <span className={`resp-ficha-tag ${tag.ativa ? 'resp-ficha-tag--notif' : 'resp-ficha-tag--off'}`}>
                      {tag.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="resp-ficha-empty">
                <CreditCard size={28} className="resp-ficha-empty-icon" />
                <p>Nenhuma tag RFID vinculada a este aluno.</p>
              </div>
            )}
            {canManageAlunos && (
              <div className="aluno-ficha-inline-form">
                <input
                  className="form-input"
                  value={novaTag}
                  onChange={(e) => setNovaTag(e.target.value)}
                  placeholder="Código da nova tag RFID"
                  onKeyDown={(e) => e.key === 'Enter' && void vincularTag()}
                />
                <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={() => void vincularTag()}>
                  Vincular tag
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TAB: Frequência === */}
      {tab === 'frequencia' && (
        <div className="aluno-ficha-tab-pad aluno-ficha-freq">
          <div className="aluno-ficha-acad-grid">
          <div className="resp-ficha-card resp-ficha-card--contato">
            <div className="aluno-ficha-freq-summary">
              <div className="aluno-ficha-freq-ring" aria-hidden>
                <svg width={88} height={88} viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="2.8"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={semaforoColor}
                    strokeWidth="2.8"
                    strokeDasharray={`${temFreq ? pct : 0}, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <span style={{ color: semaforoColor }}>{temFreq ? `${pct}%` : '—'}</span>
              </div>
              <div>
                <p className="aluno-ficha-freq-label">{semaforoLabel}</p>
                <p className="aluno-ficha-freq-meta">
                  {temFreq
                    ? `${frequencia.diasPresente} dias presente de ${frequencia.totalDias} dias letivos · ${frequencia.diasAusente} faltas`
                    : 'Nenhum dia letivo consolidado ainda para este aluno.'}
                </p>
                {frequencia.emRisco && (
                  <p className="aluno-ficha-freq-alert">
                    Comunicação ao Conselho Tutelar pode ser obrigatória (Art. 12 da LDB).
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="resp-ficha-card resp-ficha-card--contato">
            <h3 className="resp-ficha-card-title">
              <BarChart3 size={14} /> Registro de presenças
            </h3>
            {frequencia.presencas.length > 0 ? (
              <>
                <div className="aluno-ficha-freq-days">
                  {frequencia.presencas.map((p) => {
                    const tone =
                      p.status === 'PRESENTE'
                        ? 'ok'
                        : p.status === 'AUSENTE'
                          ? 'bad'
                          : 'warn';
                    return (
                      <span
                        key={p.id}
                        title={`${formatDate(p.data)} — ${p.status}`}
                        className={`aluno-ficha-freq-day aluno-ficha-freq-day--${tone}`}
                      >
                        {presencaDayNumber(p.data)}
                      </span>
                    );
                  })}
                </div>
                <div className="aluno-ficha-freq-legend">
                  <span>Presente</span>
                  <span>Atrasado / parcial</span>
                  <span>Falta</span>
                </div>
              </>
            ) : (
              <div className="resp-ficha-empty">
                <BarChart3 size={28} className="resp-ficha-empty-icon" />
                <p>Nenhum registro de presença encontrado.</p>
              </div>
            )}
          </div>
          </div>

          {frequencia.presencas.length > 0 && (
            <div className="resp-ficha-card resp-ficha-card--contato aluno-ficha-table-card">
              <div className="dt-container">
                <table className="dt-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Status</th>
                      <th>Atraso</th>
                      <th>Chamadas</th>
                      <th>Presentes</th>
                      <th>Ausências</th>
                    </tr>
                  </thead>
                  <tbody>
                    {frequencia.presencas.slice(0, 30).map((p) => (
                      <tr key={p.id} className="dt-row">
                        <td>{formatDate(p.data)}</td>
                        <td>
                          <div className="aluno-ficha-freq-status-cell">
                            <span
                              className={`resp-ficha-tag ${
                                p.status === 'PRESENTE'
                                  ? 'resp-ficha-tag--notif'
                                  : p.status === 'AUSENTE'
                                    ? 'resp-ficha-tag--off'
                                    : 'resp-ficha-tag--pri'
                              }`}
                            >
                              {p.status}
                            </span>
                            {p.inconsistencia && (
                              <span
                                className="aluno-ficha-freq-inconsist"
                                title={
                                  p.motivoInconsistencia ||
                                  p.observacoes ||
                                  'Inconsistência detectada'
                                }
                              >
                                Inconsistente
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{p.atrasoMinutos > 0 ? `+${p.atrasoMinutos} min` : '—'}</td>
                        <td>{p.totalChamadas ?? '—'}</td>
                        <td>{p.totalPresencas ?? '—'}</td>
                        <td>{p.totalAusencias ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {escolaId && id && <JustificativasAlunoPanel escolaId={escolaId} alunoId={id} />}
        </div>
      )}

      {tab === 'acoes' && !isReadOnly && (
        <div className="aluno-ficha-tab-pad">
          <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
            <div className="aluno-ficha-acoes-list">
              <div className="aluno-ficha-acao-row">
                <div>
                  <strong>LGPD</strong>
                  <p>
                    {aluno.consentimentoLGPD
                      ? `Consentimento registrado${aluno.dataConsentimento ? ` em ${new Date(aluno.dataConsentimento).toLocaleDateString('pt-BR')}` : ''}.`
                      : 'Consentimento ainda não registrado.'}
                  </p>
                  {aluno.consentimentoLGPD && aluno.ipConsentimento && (
                    <p>IP do consentimento: {aluno.ipConsentimento}</p>
                  )}
                </div>
                <span
                  className={`resp-ficha-badge ${aluno.consentimentoLGPD ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}
                >
                  {aluno.consentimentoLGPD ? 'LGPD OK' : 'LGPD pendente'}
                </span>
              </div>

              <div className="aluno-ficha-acao-row">
                <div>
                  <strong>Auditoria do cadastro</strong>
                  <p>
                    Criado em {formatDate(aluno.criadoEm)}
                    {aluno.criadoPor?.nome ? ` por ${aluno.criadoPor.nome}` : ''}
                    {' · '}
                    Atualizado em {formatDate(aluno.atualizadoEm)}
                    {aluno.atualizadoPor?.nome ? ` por ${aluno.atualizadoPor.nome}` : ''}
                  </p>
                </div>
              </div>

              {(aluno.situacao === 'TRANSFERIDO' ||
                aluno.motivoTransferencia ||
                aluno.dataTransferencia ||
                aluno.escolaDestino) && (
                <div className="aluno-ficha-acao-row">
                  <div>
                    <strong>Transferência</strong>
                    <p>
                      {aluno.motivoTransferencia || 'Motivo não informado'}
                      {aluno.dataTransferencia
                        ? ` · ${formatDate(aluno.dataTransferencia)}`
                        : ''}
                      {aluno.escolaDestino ? ` · Destino: ${aluno.escolaDestino}` : ''}
                    </p>
                  </div>
                  <span className="resp-ficha-badge resp-ficha-badge--off">Transferido</span>
                </div>
              )}

              <button
                type="button"
                className="resp-ficha-btn resp-ficha-btn--primary"
                onClick={() =>
                  void downloadPdf(
                    `/escolas/${escolaId}/alunos/${id}/pdf/termo-lgpd`,
                    `termo-lgpd-${aluno.nome}.pdf`,
                  )
                }
              >
                <Download size={14} /> Baixar termo LGPD
              </button>

              <button
                type="button"
                className="resp-ficha-btn resp-ficha-btn--primary"
                onClick={() =>
                  void downloadPdf(
                    `/escolas/${escolaId}/alunos/${id}/pdf/declaracao-matricula`,
                    `declaracao-${aluno.nome}.pdf`,
                  )
                }
              >
                <Download size={14} /> Declaração de matrícula
              </button>

              {canManageAlunos && aluno.situacao !== 'TRANSFERIDO' && (
                <button
                  type="button"
                  className="resp-ficha-btn"
                  style={{ background: '#fff', color: '#334155', border: '1px solid #e2e8f0' }}
                  onClick={() => setShowTransferModal(true)}
                >
                  <ArrowRightLeft size={14} /> Transferir aluno
                </button>
              )}

              {isModal && (
                <button
                  type="button"
                  className="resp-ficha-btn resp-ficha-btn--primary"
                  onClick={onEdit || onClose}
                >
                  <Pencil size={14} /> Editar cadastro
                </button>
              )}

              {isModal && canManageAlunos && (
                <button
                  type="button"
                  className="resp-ficha-btn resp-ficha-btn--danger"
                  onClick={onDeactivate || onClose}
                >
                  <PowerOff size={14} /> Desativar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>

      {/* Modal de Transferência */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Transferir Aluno</h2>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>Transferir <strong>{aluno.nome}</strong> para outra escola.</p>
              <div><label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Motivo da transferência *</label>
                <textarea className="form-input" value={transferMotivo} onChange={e => setTransferMotivo(e.target.value)} placeholder="Mudanca de cidade, preferencia familiar..." rows={2} style={{ resize: 'vertical' }} /></div>
              <div><label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, color: '#6b7280' }}>Escola destino (opcional)</label>
                <input className="form-input" value={transferEscola} onChange={e => setTransferEscola(e.target.value)} placeholder="Nome da escola destino" /></div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTransferModal(false)}>Cancelar</button>
              <button className="btn-save" disabled={!transferMotivo.trim() || transferLoading} onClick={async () => {
                setTransferLoading(true);
                try {
                  await api.patch(ROUTES.alunos.findOne(escolaId!, id!) + '/transferir', { motivo: transferMotivo, escolaDestino: transferEscola || undefined });
                  toast.success('Aluno transferido!');
                  queryClient.invalidateQueries({ queryKey: ['aluno', escolaId, id] });
                  setShowTransferModal(false);
                } catch { toast.error('Erro ao transferir'); } finally { setTransferLoading(false); }
              }}>{transferLoading ? 'Transferindo...' : 'Confirmar Transferência'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer mínimo no modal — ações operacionais na aba Ações */}
      {isModal && aluno && (
        <div className="resp-ficha-foot">
          <span className="resp-ficha-foot-meta">
            {aluno.criadoEm ? `Cadastrado em ${new Date(aluno.criadoEm).toLocaleDateString('pt-BR')}` : ''}
          </span>
          <span className="resp-ficha-foot-spacer" />
          <div className="resp-ficha-foot-actions">
            <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={onEdit || onClose}>
              <Pencil size={14} /> Editar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fallback legado quando não há vínculo em responsavel_aluno */
function CardInfo({ title, items }: { title: string; items: { label: string; value: string }[] }) {
  return (
    <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
      <h3 className="resp-ficha-card-title">{title}</h3>
      <dl className="resp-ficha-dl resp-ficha-dl--contato">
        {items.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Componente: Aba Boletim */
export function BoletimTab({ escolaId, alunoId }: { escolaId: string; alunoId: string }) {
  const ano = new Date().getFullYear();
  const { data, isLoading } = useQuery({
    queryKey: ['boletim', escolaId, alunoId, ano],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.alunos.findOne(escolaId, alunoId) + `?ano=${ano}`);
      return data;
    },
  });

  if (isLoading) {
    return <div className="resp-ficha-empty">Carregando boletim…</div>;
  }
  const boletim = data as any;
  const materias = boletim?.materias || [];

  return (
    <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
      <div className="aluno-ficha-section-head">
        <h3 className="resp-ficha-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
          <ClipboardList size={14} /> Ano letivo {ano}
        </h3>
        <button
          type="button"
          className="resp-ficha-btn resp-ficha-btn--primary"
          onClick={async () => {
            try {
              const res = await api.get(
                ROUTES.alunos.findOne(escolaId, alunoId) + `/pdf/boletim?ano=${ano}`,
                { responseType: 'blob' },
              );
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `boletim-${ano}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch {
              /* toast handled upstream */
            }
          }}
        >
          <Download size={14} /> Baixar PDF
        </button>
      </div>
      {materias.length === 0 ? (
        <div className="resp-ficha-empty">
          <ClipboardList size={28} className="resp-ficha-empty-icon" />
          <p>Nenhuma nota registrada para o ano letivo {ano}.</p>
        </div>
      ) : (
        <div className="dt-container aluno-ficha-table-wrap">
          <table className="dt-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Disciplina</th>
                <th style={{ textAlign: 'center' }}>1º Bim</th>
                <th style={{ textAlign: 'center' }}>2º Bim</th>
                <th style={{ textAlign: 'center' }}>3º Bim</th>
                <th style={{ textAlign: 'center' }}>4º Bim</th>
                <th style={{ textAlign: 'center' }}>Média</th>
              </tr>
            </thead>
            <tbody>
              {materias.map((m: any) => (
                <tr key={m.materiaId} className="dt-row">
                  <td style={{ fontWeight: 600 }}>{m.materiaNome}</td>
                  {[0, 1, 2, 3].map((i) => {
                    const bim = m.bimestres[i];
                    const v = bim?.media;
                    return (
                      <td key={i} style={{ textAlign: 'center', fontWeight: 600 }}>
                        {v !== null && v !== undefined ? v : '—'}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>
                    {m.mediaFinal !== null ? m.mediaFinal : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Componente: Aba Acadêmico — dados acadêmicos, turma, histórico */
export function AcademicoTab({
  aluno,
  escolaId,
  queryClient,
  canManage,
  readOnly,
}: {
  aluno: any;
  escolaId: string;
  queryClient: any;
  canManage: boolean;
  readOnly?: boolean;
}) {
  const [showChange, setShowChange] = useState(false);
  const [newTurmaId, setNewTurmaId] = useState('');
  const [filtroTurno, setFiltroTurno] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: turmas } = useQuery({
    queryKey: ['turmas-escola', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.turmas.listar(escolaId));
      return data as any[];
    },
  });

  const { data: turnos } = useQuery({
    queryKey: ['turnos-escola', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.turnos.listar(escolaId));
      return data as any[];
    },
  });

  const historico = aluno.historicoTurmas || [];
  const situacaoLabel =
    SITUACAO_LABELS[aluno.situacao || 'ATIVO']?.label || aluno.situacao || 'Ativo';
  const temTransferencia = !!(
    aluno.situacao === 'TRANSFERIDO' ||
    aluno.motivoTransferencia ||
    aluno.dataTransferencia ||
    aluno.escolaDestino
  );

  const handleChangeTurma = async () => {
    if (!newTurmaId) return;
    setLoading(true);
    try {
      await api.patch(ROUTES.alunos.findOne(escolaId, aluno.id) + '/turma', { turmaId: newTurmaId });
      toast.success('Turma alterada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['aluno', escolaId, aluno.id] });
      setShowChange(false);
      setNewTurmaId('');
      setFiltroTurno('');
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg[0] : 'Erro ao alterar turma');
    } finally {
      setLoading(false);
    }
  };

  const selectedTurma = newTurmaId ? (turmas || []).find((t: any) => t.id === newTurmaId) : null;
  const selCount = selectedTurma?._count?.alunos ?? 0;
  const selCap = selectedTurma?.capacidade ?? 0;
  const selVagas = selCap > 0 ? selCap - selCount : -1;
  const selLotada = selCap > 0 && selCount >= selCap;

  return (
    <div className="aluno-ficha-stack">
      <div className={`aluno-ficha-acad-grid${showChange ? ' aluno-ficha-acad-grid--expand' : ''}`}>
      <div className="resp-ficha-card resp-ficha-card--contato">
        <h3 className="resp-ficha-card-title">
          <BookOpen size={14} /> Dados acadêmicos
        </h3>
        <dl className="resp-ficha-dl resp-ficha-dl--contato">
          <div>
            <dt>Matrícula</dt>
            <dd>{aluno.matricula || '—'}</dd>
          </div>
          <div>
            <dt>Ingresso</dt>
            <dd>{aluno.anoIngresso != null ? String(aluno.anoIngresso) : '—'}</dd>
          </div>
          <div>
            <dt>NIS</dt>
            <dd>{readOnly ? '—' : aluno.nis || '—'}</dd>
          </div>
          <div>
            <dt>Situação</dt>
            <dd>{situacaoLabel}</dd>
          </div>
        </dl>
        {temTransferencia && (
          <dl className="resp-ficha-dl resp-ficha-dl--contato aluno-ficha-transfer-dl">
            <div className="resp-ficha-dl-span">
              <dt>Motivo da transferência</dt>
              <dd>{aluno.motivoTransferencia || '—'}</dd>
            </div>
            <div>
              <dt>Data da transferência</dt>
              <dd>{formatPresencaDate(aluno.dataTransferencia)}</dd>
            </div>
            <div>
              <dt>Escola destino</dt>
              <dd>{aluno.escolaDestino || '—'}</dd>
            </div>
          </dl>
        )}
      </div>

      <div className="resp-ficha-card resp-ficha-card--contato">
        <div className="aluno-ficha-section-head">
          <h3 className="resp-ficha-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
            <BookOpen size={14} /> Turma atual
          </h3>
          {canManage && (
            <button
              type="button"
              className="resp-ficha-btn resp-ficha-btn--primary"
              onClick={() => setShowChange(!showChange)}
            >
              <ArrowRightLeft size={14} />
              {showChange ? 'Cancelar' : 'Mudar turma'}
            </button>
          )}
        </div>

        {aluno.turma ? (
          <dl className="resp-ficha-dl resp-ficha-dl--contato">
            <div>
              <dt>Turma</dt>
              <dd>{aluno.turma.nome}</dd>
            </div>
            <div>
              <dt>Série</dt>
              <dd>{aluno.turma.serie || '—'}</dd>
            </div>
            <div>
              <dt>Turno</dt>
              <dd>{aluno.turma.turnoOperacional?.nome || aluno.turma.turno || '—'}</dd>
            </div>
          </dl>
        ) : (
          <div className="resp-ficha-empty">
            <BookOpen size={28} className="resp-ficha-empty-icon" />
            <p>Aluno sem turma vinculada.</p>
          </div>
        )}

        {aluno.turma?.ativo === false && (
          <p className="aluno-ficha-inline-note aluno-ficha-inline-note--warn">
            Esta turma está <strong>inativa</strong> e não entra na chamada dos professores. Use{' '}
            <strong>Mudar turma</strong> para uma turma ativa.
          </p>
        )}

        {showChange && (
          <div className="aluno-ficha-inline-form aluno-ficha-inline-form--stack">
            <div className="aluno-ficha-form-row">
              <div className="aluno-ficha-form-field aluno-ficha-form-field--sm">
                <label>Turno</label>
                <select
                  className="form-input"
                  value={filtroTurno}
                  onChange={(e) => {
                    setFiltroTurno(e.target.value);
                    setNewTurmaId('');
                  }}
                >
                  <option value="">Todos os turnos</option>
                  {[
                    ...new Set([
                      ...(turmas || []).map((t: any) => t.turnoOperacional?.nome || t.turno),
                      ...(turnos || []).map((t: any) => t.nome)
                    ].filter(Boolean)),
                  ].map((turno: any) => (
                    <option key={turno} value={turno}>
                      {turno}
                    </option>
                  ))}
                </select>
              </div>
              <div className="aluno-ficha-form-field">
                <label>Nova turma</label>
                <select
                  className="form-input"
                  value={newTurmaId}
                  onChange={(e) => setNewTurmaId(e.target.value)}
                >
                  <option value="">Selecione a turma…</option>
                  {(turmas || [])
                    .filter((t: any) => {
                      if (t.ativo === false) return false;
                      if (t.id === aluno.turmaId) return false;
                      if (filtroTurno && (t.turnoOperacional?.nome || t.turno) !== filtroTurno) {
                        return false;
                      }
                      return true;
                    })
                    .map((t: any) => {
                      const horarios = t.horarios?.length ?? t._count?.horarios ?? 0;
                      const alunosN = t._count?.alunos ?? 0;
                      return (
                        <option key={t.id} value={t.id}>
                          {t.nome} — {t.serie || 's/ série'} (
                          {t.turnoOperacional?.nome || t.turno || '—'}) · {alunosN} aluno
                          {alunosN !== 1 ? 's' : ''}
                          {horarios === 0 ? ' · sem grade' : ''}
                        </option>
                      );
                    })}
                </select>
              </div>
            </div>

            {selectedTurma && (
              <div
                className={`aluno-ficha-turma-preview${selLotada ? ' aluno-ficha-turma-preview--bad' : ''}`}
              >
                <div>
                  <strong>
                    {selectedTurma.nome}
                    {selectedTurma.serie ? ` — ${selectedTurma.serie}` : ''}
                  </strong>
                  <p>
                    Turno: {selectedTurma.turnoOperacional?.nome || selectedTurma.turno || '—'}
                    {' · '}
                    Sala: {selectedTurma.sala || '—'}
                    {' · '}
                    Alunos: {selCount}
                    {selCap > 0 ? `/${selCap}` : ''}
                  </p>
                </div>
                <span className={`resp-ficha-tag ${selLotada ? 'resp-ficha-tag--off' : 'resp-ficha-tag--notif'}`}>
                  {selLotada ? 'Superlotada' : selVagas >= 0 ? `${selVagas} vagas` : 'Sem limite'}
                </span>
              </div>
            )}

            <div className="aluno-ficha-form-actions">
              <button
                type="button"
                className="resp-ficha-btn"
                onClick={() => {
                  setShowChange(false);
                  setFiltroTurno('');
                  setNewTurmaId('');
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`resp-ficha-btn resp-ficha-btn--primary${!newTurmaId || loading ? ' resp-ficha-btn--disabled' : ''}`}
                onClick={() => void handleChangeTurma()}
                disabled={!newTurmaId || loading}
              >
                {loading ? 'Salvando…' : 'Confirmar mudança'}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
        <h3 className="resp-ficha-card-title">
          <ArrowRightLeft size={14} /> Histórico de turmas
        </h3>
        {historico.length > 0 ? (
          <ul className="aluno-ficha-list">
            {historico.map((h: any) => (
              <li key={h.id} className="aluno-ficha-list-item">
                <BookOpen size={16} className="aluno-ficha-list-icon" />
                <div className="aluno-ficha-list-main">
                  <span className="aluno-ficha-list-title">
                    {h.turma?.nome || '—'}
                    {h.turma?.serie ? ` — ${h.turma.serie}` : ''}
                  </span>
                  <span className="aluno-ficha-list-meta">
                    {h.turma?.turnoOperacional?.nome || h.turma?.turno || '—'}
                    {' · '}
                    Ano {h.anoLetivo}
                  </span>
                </div>
                {h.atual && <span className="resp-ficha-tag resp-ficha-tag--notif">Atual</span>}
              </li>
            ))}
          </ul>
        ) : (
          <div className="resp-ficha-empty">
            <ArrowRightLeft size={28} className="resp-ficha-empty-icon" />
            <p>Nenhum histórico de turmas registrado.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Componente: Aba Responsável — consulta + modal canônico de edição */
export function ResponsavelTab({ aluno, escolaId, queryClient, canManage }: { aluno: any; escolaId: string; queryClient: any; canManage: boolean }) {
  const [editTarget, setEditTarget] = useState<ResponsavelFormModel | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [fichaId, setFichaId] = useState<string | null>(null);
  const parentescoLabels: Record<string, string> = {
    PAI: 'Pai',
    MAE: 'Mãe',
    AVO: 'Avô/Avó',
    TIO: 'Tio/Tia',
    IRMAO: 'Irmão/Irmã',
    OUTRO: 'Outro',
  };

  const toFormModel = (r: any, ra: any): ResponsavelFormModel => ({
    id: r.id,
    nome: r.nome || '',
    email: r.email || '',
    telefone: r.telefone || '',
    fotoUrl: r.fotoUrl,
    cpf: r.cpf,
    parentesco: parentescoLabels[ra.grauParentesco || ''] || ra.grauParentesco || r.parentesco || '',
    dataNascimento: r.dataNascimento,
    usuarioId: r.usuarioId,
    alunos: [
      {
        aluno: {
          id: aluno.id,
          nome: aluno.nome,
          matricula: aluno.matricula,
          fotoUrl: aluno.fotoUrl,
          turma: aluno.turma
            ? {
                id: aluno.turma.id,
                nome: aluno.turma.nome,
                serie: aluno.turma.serie,
                turno: aluno.turma.turno,
              }
            : null,
        },
        grauParentesco: ra.grauParentesco,
        principal: ra.principal,
      },
    ],
  });

  const openEdit = (r: any, ra: any) => {
    setEditTarget(toFormModel(r, ra));
    setEditOpen(true);
  };

  return (
    <div className="aluno-ficha-stack">
      {aluno.responsaveis && aluno.responsaveis.length > 0 ? (
        aluno.responsaveis.map((ra: any) => {
          const r = ra.responsavel;
          if (!r) return null;
          const appStatus = getResponsavelAppStatus({
            ...r,
            alunos: [{ id: aluno.id }],
            _count: { alunos: 1 },
          });
          return (
            <div
              key={ra.id}
              className={`resp-ficha-card resp-ficha-card--full resp-ficha-card--contato${ra.principal ? ' aluno-ficha-resp-card--principal' : ''}`}
            >
              <div className="aluno-ficha-section-head">
                <div className="aluno-ficha-resp-head-text">
                  <h3 className="resp-ficha-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
                    <Heart size={14} /> {r.nome}
                  </h3>
                  <div className="aluno-ficha-resp-tags">
                    {ra.principal && (
                      <span className="resp-ficha-tag resp-ficha-tag--pri">Principal</span>
                    )}
                    <AppStatusBadge status={appStatus} />
                  </div>
                </div>
                <div className="aluno-ficha-resp-actions">
                  <button
                    type="button"
                    className="resp-ficha-btn"
                    onClick={() => setFichaId(r.id)}
                  >
                    <Eye size={14} /> Ver ficha
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      className="resp-ficha-btn resp-ficha-btn--primary"
                      onClick={() => openEdit(r, ra)}
                    >
                      <Pencil size={14} /> Editar
                    </button>
                  )}
                </div>
              </div>

              <dl className="resp-ficha-dl resp-ficha-dl--contato">
                <div>
                  <dt>Parentesco</dt>
                  <dd>
                    {parentescoLabels[ra.grauParentesco || ''] || ra.grauParentesco || '—'}
                  </dd>
                </div>
                <div>
                  <dt>Telefone</dt>
                  <dd>{formatPhone(r.telefone || '') || r.telefone || '—'}</dd>
                </div>
                <div className="resp-ficha-dl-span">
                  <dt>E-mail</dt>
                  <dd>{r.email || '—'}</dd>
                </div>
                <div>
                  <dt>CPF</dt>
                  <dd>{r.cpf ? formatCPF(r.cpf) : '—'}</dd>
                </div>
                <div>
                  <dt>Data de nascimento</dt>
                  <dd>
                    {r.dataNascimento
                      ? (() => {
                          const d = new Date(r.dataNascimento);
                          if (!isNaN(d.getTime())) {
                            const day = String(d.getUTCDate()).padStart(2, '0');
                            const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                            const year = d.getUTCFullYear();
                            return `${day}/${month}/${year}`;
                          }
                          return r.dataNascimento;
                        })()
                      : '—'}
                  </dd>
                </div>
              </dl>

              {canManage && (
                <div className="aluno-ficha-resp-app">
                  <ResponsavelAcessoAppPanel
                    responsavel={{
                      ...r,
                      alunos: [{ id: aluno.id }],
                      _count: { alunos: 1 },
                    }}
                    escolaId={escolaId}
                    canManage={canManage}
                    compact
                    onInvited={() => void queryClient.invalidateQueries({ queryKey: ['aluno'] })}
                  />
                </div>
              )}
            </div>
          );
        })
      ) : (
        <CardInfo
          title="Responsável (dados legados)"
          items={[
            { label: 'Nome', value: aluno.nomeResponsavel || 'Nenhum responsável vinculado' },
            { label: 'Parentesco', value: aluno.parentesco || '—' },
            { label: 'CPF', value: aluno.cpfResponsavel || '—' },
            { label: 'Telefone', value: aluno.telefoneResponsavel || '—' },
            { label: 'E-mail', value: aluno.emailResponsavel || '—' },
          ]}
        />
      )}

      <ResponsavelFormModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditTarget(null);
        }}
        escolaId={escolaId}
        responsavel={editTarget}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['aluno'] });
          setEditOpen(false);
          setEditTarget(null);
        }}
      />

      <Modal
        open={!!fichaId}
        onClose={() => setFichaId(null)}
        title="Ficha do responsável"
        size="wide"
        contentClassName="ac-ficha-modal ac-responsavel-ficha-modal"
        hideCloseButton
      >
        {fichaId ? (
          <ResponsavelFichaDesktop
            responsavelId={fichaId}
            escolaId={escolaId}
            canManage={canManage}
            onClose={() => setFichaId(null)}
            onEdit={() => {
              const ra = aluno.responsaveis?.find((x: any) => x.responsavel?.id === fichaId);
              const r = ra?.responsavel;
              if (!r || !ra) return;
              setFichaId(null);
              openEdit(r, ra);
            }}
            onDeactivate={() => setFichaId(null)}
            onReactivate={() => setFichaId(null)}
            canDeactivate={{ ok: false, reason: 'Use a gestão de Responsáveis para desativar.' }}
            onInvited={() => void queryClient.invalidateQueries({ queryKey: ['aluno'] })}
          />
        ) : null}
      </Modal>
    </div>
  );
}
